import { useState } from "react";
import { Info } from "lucide-react";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import { AGENT_CARD_INFO_TEXTS, type AgentCardInfoKey } from "./agentCardInfoTexts";

interface AgentCardInfoProps {
  infoKey: AgentCardInfoKey;
  className?: string;
  variant?: "overlay" | "inline";
}

const FALLBACK_INFO =
  "Informação temporariamente indisponível. Recarregue a página ou reinicie o servidor de desenvolvimento.";

export function AgentCardInfo({ infoKey, className, variant = "overlay" }: AgentCardInfoProps) {
  const [popoverOpen, setPopoverOpen] = useState(false);
  const content = AGENT_CARD_INFO_TEXTS[infoKey] ?? FALLBACK_INFO;

  const buttonClassName = cn(
    variant === "inline"
      ? "inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-muted-foreground/60 hover:text-muted-foreground hover:bg-muted/60 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      : "absolute top-3 right-3 sm:top-4 sm:right-4 z-10 inline-flex h-6 w-6 items-center justify-center rounded-full text-muted-foreground/60 hover:text-muted-foreground hover:bg-muted/60 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
    className,
  );

  return (
    <Popover open={popoverOpen} onOpenChange={setPopoverOpen}>
      <Tooltip open={popoverOpen ? false : undefined} delayDuration={300}>
        <TooltipTrigger asChild>
          <PopoverTrigger asChild>
            <button type="button" aria-label="Informações sobre este bloco" className={buttonClassName}>
              <Info className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
            </button>
          </PopoverTrigger>
        </TooltipTrigger>
        <TooltipContent side="left" align="end" className="max-w-[280px] text-xs leading-relaxed">
          {content}
        </TooltipContent>
      </Tooltip>
      <PopoverContent
        side="left"
        align="end"
        className="max-w-[280px] text-xs leading-relaxed"
        onOpenAutoFocus={(event) => event.preventDefault()}
      >
        <p className="whitespace-pre-wrap">{content}</p>
      </PopoverContent>
    </Popover>
  );
}
